import { useMemo } from "react";
import { Marker, InfoWindow } from "@vis.gl/react-google-maps";
import { CalendarDays, Clock, ChevronRight, X } from "lucide-react";
import { danceStyleColor } from "@/lib/constants";
import { useDanceStyles } from "@/hooks/useDanceStyles";
import { formatTime, formatShortDate } from "@/lib/dateUtils";
import { EventStyleBadges } from "@/components/calendar/DanceStyleBadge";
import { seriesLabel, countThrough, isoMonthsFromNow } from "@/lib/series";
import type { Series } from "@/lib/series";
import type { Doc } from "convex/_generated/dataModel";

export type VenueGroup = {
  key: string;
  venueName: string;
  venueId?: string;
  lat: number;
  lng: number;
  events: Doc<"events">[];
  series: Series<Doc<"events">>[];
};

interface VenueMarkerProps {
  group: VenueGroup;
  isSelected: boolean;
  onSelect: () => void;
  onClose: () => void;
  onViewEvent: (event: Doc<"events">) => void;
  onViewVenue: (venueId: string) => void;
}

/**
 * One pin per venue.
 *
 * Previously every occurrence got its own marker, so a venue running five
 * weekly series carried ninety-nine markers stacked on one coordinate. Google
 * gives the last-added marker the highest z-index and events arrive
 * date-ascending, so clicking a venue opened its *furthest-future* occurrence —
 * a date up to two years out.
 */
function makeSvgIcon(color: string, count: number, selected: boolean): google.maps.Icon {
  const stroke = selected ? "#ffffff" : "#1a1a1a";
  // Multi-series venues get a slightly larger pin so density is legible before
  // anything is clicked.
  const size = (selected ? 28 : 22) + Math.min(count - 1, 3) * 2;
  const r = size / 2 - 2;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="${color}" stroke="${stroke}" stroke-width="2.5"/></svg>`;
  return {
    url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  };
}

export function VenueMarker({
  group,
  isSelected,
  onSelect,
  onClose,
  onViewEvent,
  onViewVenue,
}: VenueMarkerProps) {
  const soonest = group.series[0]?.next;
  const { styleMap, orderStyles } = useDanceStyles();
  const markerColor = danceStyleColor(orderStyles(soonest?.danceStyles, soonest?.mainStyles), styleMap);

  const markerIcon = useMemo(
    () => makeSvgIcon(markerColor, group.series.length, isSelected),
    [markerColor, group.series.length, isSelected]
  );

  const through = useMemo(() => isoMonthsFromNow(3), []);
  const upcomingCount = group.events.filter((e) => e.date <= through).length;

  if (!soonest) return null;

  return (
    <>
      <Marker
        position={{ lat: group.lat, lng: group.lng }}
        onClick={onSelect}
        title={group.venueName}
        icon={markerIcon}
      />

      {isSelected && (
        <InfoWindow
          position={{ lat: group.lat, lng: group.lng }}
          onCloseClick={onClose}
          headerDisabled
        >
          <div className="min-w-[250px] max-w-[310px] overflow-hidden">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-semibold text-gray-900 leading-tight text-sm">
                {group.venueName}
              </h3>
              <button
                onClick={onClose}
                className="shrink-0 p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-3">
              <CalendarDays className="w-3 h-3 text-gray-400 shrink-0" />
              <span>
                {upcomingCount} event{upcomingCount !== 1 ? "s" : ""} in the next
                3 months
              </span>
            </div>

            {/* The next occurrence's own details, so a per-date change — this
                month's band, a parking note — shows without the series being
                split apart. */}
            <button
              onClick={() => onViewEvent(soonest)}
              className="w-full text-left rounded border border-gray-200 p-2 mb-2 hover:bg-gray-50 transition-colors"
            >
              <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">
                Next
              </div>
              <div className="text-sm font-medium text-gray-900 leading-tight">
                {soonest.title}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-600 mt-1">
                <Clock className="w-3 h-3 text-gray-400 shrink-0" />
                <span>
                  {formatShortDate(soonest.date)} · {formatTime(soonest.startTime)}
                </span>
              </div>
              {group.series[0]?.isRecurring && (
                <div className="text-[11px] text-gray-500 mt-0.5">
                  {seriesLabel(group.series[0]!)} ·{" "}
                  {countThrough(group.series[0]!, through)} in next 3 months
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-1.5">
                <EventStyleBadges danceStyles={soonest.danceStyles} mainStyles={soonest.mainStyles} limit={3} />
              </div>
            </button>

            {group.series.length > 1 && (
              <div className="text-xs text-gray-500 mb-2">
                +{group.series.length - 1} other series at this venue
              </div>
            )}

            {group.venueId && (
              <button
                onClick={() => onViewVenue(group.venueId!)}
                className="flex items-center justify-center gap-1 w-full py-1.5 px-3 bg-gray-100 hover:bg-gray-200 rounded text-xs font-medium text-gray-700 transition-colors"
              >
                View all at this venue
                <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </InfoWindow>
      )}
    </>
  );
}
